import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/api';
import {
  createKmEntityType,
  deleteKmEntityType,
  fetchKmAttrTypes,
  fetchKmEntityType,
  fetchKmEntityTypeAttrs,
  fetchKmEntityTypes,
  fetchKmRelationTypes,
  updateKmEntityType,
} from './api';
import type { KmAttrTypesParams, KmEntityTypesParams, KmRelationTypesParams } from '../types';

const KM_ROOT = 'km';
const ENTITY_TYPES = 'entity-types';

/**
 * Ключи кэша TanStack Query для модели знаний.
 */
export const kmKeys = {
  all: [KM_ROOT] as const,
  entityTypeLists: () => [KM_ROOT, ENTITY_TYPES, 'list'] as const,
  entityTypeList: (params?: KmEntityTypesParams) => [KM_ROOT, ENTITY_TYPES, 'list', { ...params }] as const,
  entityTypeDetail: (code?: string) => [KM_ROOT, ENTITY_TYPES, 'detail', code] as const,
  entityTypeAttrs: (code?: string) => [KM_ROOT, ENTITY_TYPES, 'attrs', code] as const,
  attrTypeList: (params?: KmAttrTypesParams) => [KM_ROOT, 'attr-types', 'list', { ...params }] as const,
  relationTypeList: (params?: KmRelationTypesParams) => [KM_ROOT, 'relation-types', 'list', { ...params }] as const,
};

// Запрос активен только для реального кода типа (не в режиме создания)
const isRealCode = (code?: string) => !!code && code !== 'create';

/**
 * Хук списка типов сущностей.
 */
export const useKmEntityTypes = (params?: KmEntityTypesParams) =>
  useQuery({
    queryKey: kmKeys.entityTypeList(params),
    queryFn: () => fetchKmEntityTypes(params),
  });

/**
 * Хук карточки типа сущности по коду.
 */
export const useKmEntityType = (code?: string) =>
  useQuery({
    queryKey: kmKeys.entityTypeDetail(code),
    queryFn: () => fetchKmEntityType(code!),
    enabled: isRealCode(code),
  });

/**
 * Хук схемы типа (привязок атрибутов) по коду типа.
 */
export const useKmEntityTypeAttrs = (code?: string) =>
  useQuery({
    queryKey: kmKeys.entityTypeAttrs(code),
    queryFn: () => fetchKmEntityTypeAttrs(code!),
    enabled: isRealCode(code),
  });

/**
 * Хук справочника типов атрибутов (имя и тип данных для схемы).
 */
export const useKmAttrTypes = (params?: KmAttrTypesParams) =>
  useQuery({
    queryKey: kmKeys.attrTypeList(params),
    queryFn: () => fetchKmAttrTypes(params),
  });

/**
 * Хук списка типов связей; активен при заданном фильтре from/to.
 */
export const useKmRelationTypes = (params?: KmRelationTypesParams) =>
  useQuery({
    queryKey: kmKeys.relationTypeList(params),
    queryFn: () => fetchKmRelationTypes(params),
    enabled: isRealCode(params?.from ?? params?.to),
  });

/**
 * Мутация создания типа сущности; инвалидирует список.
 */
export const useCreateKmEntityType = () =>
  useMutation({
    mutationFn: createKmEntityType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });

/**
 * Мутация изменения типа сущности; обновляет detail-кэш и инвалидирует список.
 */
export const useUpdateKmEntityType = () =>
  useMutation({
    mutationFn: updateKmEntityType,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(kmKeys.entityTypeDetail(variables.code), data);
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });

/**
 * Мутация мягкого удаления типа сущности; чистит detail-кэш и инвалидирует список.
 */
export const useDeleteKmEntityType = () =>
  useMutation({
    mutationFn: deleteKmEntityType,
    onSuccess: (_data, code) => {
      queryClient.removeQueries({ queryKey: kmKeys.entityTypeDetail(code) });
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });
